import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Button, Paper, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,} from "@mui/material";
import NoRowsLayout from "../../components/Layout/NoRowsLayout";
import Loading from "../../components/Loader/Loading";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { Add as AddIcon, Delete as DeleteIcon, Check as CheckIcon, ArrowBack, Add,} from "@mui/icons-material";
import { DataGrid, GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import MultiColumnFilter from "../../components/Filter/MultiColumnFilter";
import ExportAll from "./ExportAll";
import QuickSearchTool from "../../components/QuickSearch/QuickSearchTool";
import CustomPagination from "../../components/pagination/CustomPagination";
import { SampleServicesCrud } from "./SampleServiceCrud";

const mockData = [
  {
      "id": 2,
      "servicesMasterId": 624,
      "servicesChildId": 625,
      "serviceBrandId": 4,
      "serviceTime": 10,
      "additionalData": {
          "low": 1000,
          "mid": 800,
          "high": 900
      },
      "childService": {
          "id": 625,
          "parentServiceId": 624,
          "title": "Men's & Kids' Salon Beard Trimming & Styling",
          "serviceTime": 1,
          "parentService": {
              "id": 624,
              "title": "Beard Trimming & Styling"
          },
          "ServiceCategoryRels": [
              {
                  "serviceCategoryId": 169,
                  "ServiceCategory": {
                      "id": 169,
                      "title": "Hair Cut & Beard Styling",
                      "cat": {
                          "id": 167,
                          "title": "Men's & Kids' Salon"
                      }
                  }
              }
          ]
      },
      "brd": {
          "id": 4,
          "title": "Lakme"
      }
  },
  {
      "id": 6,
      "servicesMasterId": 695,
      "servicesChildId": 696,
      "serviceBrandId": 23,
      "serviceTime": 10,
      "additionalData": null,
      "childService": {
          "id": 696,
          "parentServiceId": 695,
          "title": "Full Arms + Underarms Waxing - Honey",
          "serviceTime": 1,
          "parentService": {
              "id": 695,
              "title": "Waxing"
          },
          "ServiceCategoryRels": [
              {
                  "serviceCategoryId": 177,
                  "ServiceCategory": {
                      "id": 177,
                      "title": "Salon",
                      "cat": {
                          "id": 175,
                          "title": "Women's Salon & Spa"
                      }
                  }
              }
          ]
      },
      "brd": {
          "id": 23,
          "title": "O3+ Professional"
      }
  },
  {
      "id": 7,
      "servicesMasterId": 695,
      "servicesChildId": 696,
      "serviceBrandId": 6,
      "serviceTime": 10,
      "additionalData": {
          "low": 1000,
          "mid": 800,
          "high": 900
      },
      "childService": {
          "id": 696,
          "parentServiceId": 695,
          "title": "Full Arms + Underarms Waxing - Honey",
          "serviceTime": 1,
          "parentService": {
              "id": 695,
              "title": "Waxing"
          },
          "ServiceCategoryRels": [
              {
                  "serviceCategoryId": 177,
                  "ServiceCategory": {
                      "id": 177,
                      "title": "Salon",
                      "cat": {
                          "id": 175,
                          "title": "Women's Salon & Spa"
                      }
                  }
              }
          ]
      },
      "brd": {
          "id": 6,
          "title": "Mamaearth"
      }
  },
  {
      "id": 5,
      "servicesMasterId": 832,
      "servicesChildId": 833,
      "serviceBrandId": 1,
      "serviceTime": 50,
      "additionalData": {
          "low": 1000,
          "mid": 800,
          "high": 900
      },
      "childService": {
          "id": 833,
          "parentServiceId": 832,
          "title": "Electrical Appliances Air Cooler Check Up & Repair",
          "serviceTime": 1,
          "parentService": {
              "id": 832,
              "title": "Air Cooler Check Up & Repair"
          },
          "ServiceCategoryRels": [
              {
                  "serviceCategoryId": 186,
                  "ServiceCategory": {
                      "id": 186,
                      "title": "Air Cooler",
                      "cat": {
                          "id": 185,
                          "title": "Electrical Appliances"
                      }
                  }
              }
          ]
      },
      "brd": {
          "id": 1,
          "title": "Generic"
      }
  }
]

const DataGridEditor = () => {
  const [data, setData] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  // Grid API reference for programmatic focus/edit control
  const apiRef = useGridApiRef();

  // Add Column dialog state
  const [addColDialogOpen, setAddColDialogOpen] = useState(false);
  const [newColName, setNewColName] = useState("");
  const [newColError, setNewColError] = useState("");

  const openAddColumnDialog = () => {
    setNewColName("");
    setNewColError("");
    setAddColDialogOpen(true);
  };

  const closeAddColumnDialog = () => {
    setAddColDialogOpen(false);
  };

  const confirmAddColumn = () => {
    const raw = (newColName || "").trim();
    if (!raw) {
      setNewColError("Please enter a column name");
      return;
    }

    // sanitize into a safe key: replace spaces with underscore
    const key = raw.replace(/\s+/g, "_");

    const reserved = ["id", "name", "description", "actions"];
    if (reserved.includes(key)) {
      setNewColError("This column name is reserved");
      return;
    }

    if (allColumns.includes(key)) {
      setNewColError("Column already exists");
      return;
    }

    // add to allColumns and update each row.additionalData
    setAllColumns((prev) => {
      const next = [...prev, key];
      return Array.from(new Set(next)).sort();
    });

    setData((prev) =>
      prev.map((row) => ({
        ...row,
        additionalData: {
          ...(row.additionalData || {}),
          [key]: "",
        },
      }))
    );

    setShowSuccess(true);
    setAddColDialogOpen(false);
  };

  // Handle Tab key to move to next editable cell and enter edit mode
  const handleCellKeyDown = useCallback(
    (params, event) => {
      if (event.key === "Tab") {
        // prevent browser default tabbing
        event.preventDefault();

        const allCols = apiRef.current.getAllColumns
          ? apiRef.current.getAllColumns()
          : [];
        // pick editable fields only and exclude action column and id
        const editableFields = allCols
          .filter(
            (c) =>
              c.field &&
              c.field !== "actions" &&
              c.field !== "id" &&
              c.editable !== false
          )
          .map((c) => c.field);

        const currentIndex = editableFields.indexOf(params.field);
        if (currentIndex === -1) return;

        let nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
        if (nextIndex < 0) nextIndex = editableFields.length - 1;
        if (nextIndex >= editableFields.length) nextIndex = 0; // wrap around within row

        const nextField = editableFields[nextIndex];
        if (!nextField) return;

        // Start edit mode on the next cell
        // newEditingApi must be enabled for these methods to exist
        try {
          apiRef.current.startCellEditMode?.({
            id: params.id,
            field: nextField,
          });
          // fallback: set focus to the cell (some versions expose setCellFocus)
          apiRef.current.setCellFocus?.({ id: params.id, field: nextField });
        } catch (err) {
          // ignore if API not available
        }
      }
    },
    [apiRef]
  );

  // Extract all unique columns from additionalData
  useEffect(() => {
    const columns = new Set();
    data.forEach((row) => {
      Object.keys(row.additionalData || {}).forEach((key) => columns.add(key));
    });

    const discovered = Array.from(columns);
    // Merge discovered keys into existing allColumns while preserving
    // previously-chosen/insertion order so newly added columns stay
    // at the end (just before the actions/delete column).
    setAllColumns((prev) => {
      if (!prev || prev.length === 0) return discovered;
      const existing = new Set(prev);
      const merged = [...prev];
      discovered.forEach((k) => {
        if (!existing.has(k)) {
          merged.push(k);
          existing.add(k);
        }
      });
      return merged;
    });
  }, [data]);

  // Flatten data for DataGrid
  const rows = useMemo(() => {
    return data.map((row) => ({
      id: row.id,
      masterService: row.childService?.parentService?.title || "",
      service: row.childService?.title || "",
      brand: row.brd?.title || "",
      serviceTime: row.serviceTime,
      ...(row.additionalData || {}),
    }));
  }, [data]);

  // Define columns dynamically
  const columns = useMemo(() => {
    const deleteColumn = {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => deleteRow(params.id)}
          color="error"
        />,
      ],
    };

    return [
      {
        field: "id",
        headerName: "ID",
        width: 70,
        minWidth: 70,
        maxWidth: 90,
        editable: false,
        headerClassName: "header-fixed",
        cellClassName: "cell-id",
        resizable: true,
      },
      {
        field: "masterService",
        headerName: "Master Service",
        minWidth: 250,
        flex: 1,
        // editable: true,
        headerClassName: "header-fixed",
        resizable: true,
      },
      {
        field: "service",
        headerName: "Child Service",
        minWidth: 250,
        flex: 2,
        // editable: true,
        headerClassName: "header-fixed",
        resizable: true,
      },
      {
        field: "brand",
        headerName: "Service Brand",
        minWidth: 150,
        flex: 2,
        // editable: true,
        headerClassName: "header-fixed",
        resizable: true,
      },
      {
        field: "serviceTime",
        headerName: "Service Time (in mins.)",
        minWidth: 80,
        flex: 2,
        editable: true,
        headerClassName: "header-fixed",
        resizable: true,
      },
      ...allColumns.map((col) => ({
        field: col,
        headerName: col.charAt(0).toUpperCase() + col.slice(1),
        minWidth: 120,
        flex: 1,
        editable: true,
        headerClassName: "header-dynamic",
        resizable: true,
      })),
      deleteColumn,
    ];
  }, [allColumns]);

  // Handle row updates
  const processRowUpdate = async (newRow, oldRow) => {
    // newRow/oldRow are flattened objects (grid columns). Convert to the
    // structured shape used by our mockData: { id, name, description, additionalData: {...} }
    // Check if there are actual changes
    const hasChanges = JSON.stringify(newRow) !== JSON.stringify(oldRow);

    if (!hasChanges) {
      return oldRow; // No changes, don't save
    }

    const { id, masterService, service, brand, serviceTime, ...additionalData } = newRow;
    const updatedRowData = {
      id,
       masterService, service, brand, serviceTime,
      additionalData: additionalData,
    };

    // Log the structured row so console output matches mockData shape
    console.log('updatedRowData', updatedRowData);

    const updatedData = data.map((row) => (row.id === id ? updatedRowData : row));
    setData(updatedData);

    try{
      const updateResponse = await SampleServicesCrud.updateServiceById(id, updatedRowData);
      // if(updateResponse.success == '1'){
      //   setShowSuccess(true);
      // }
    }catch(error){
      console.log(error);
    }

    //   // Call your backend API
    //   try {
    //     const response = await fetch(`/api/data-sheets/${id}`, {
    //       method: 'PUT',
    //       headers: { 'Content-Type': 'application/json' },
    //       body: JSON.stringify(updatedRowData)
    //     });

    //     if (!response.ok) throw new Error('Failed to save');

    //     setShowSuccess(true);
    //   } catch (error) {
    //     console.error('Save error:', error);
    //     // Show error message
    //   }
    setShowSuccess(true);
    return newRow;
  };

  const handleProcessRowUpdateError = (error) => {
    console.error("Row update error:", error);
  };

  // Add new row
  const addNewRow = () => {
    const newId = Math.max(...data.map((r) => r.id)) + 1;
    const newJsonData = {};
    allColumns.forEach((col) => {
      newJsonData[col] = "";
    });

    const newRow = {
      id: newId,
      name: "New Record",
      description: "",
      additionalData: newJsonData,
    };

    setData([...data, newRow]);
    setShowSuccess(true);
  };

  // Add

  // Delete row
  const deleteRow = (id) => {
    setRowToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    setData(data.filter((row) => row.id !== rowToDelete));
    setShowSuccess(true);
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };
  const navigate = useNavigate();

  const filterableColumns=[];
  const handleApplyFilter = (selectedFilters) => {
      
  }
  const [clearMultiFilter, setClearMultiFilter] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
    filters: {
      searchKey: "",
      sortBy: 'createdAt',
      sortType: 'desc',
      filterOptions: []
    }
  })
    /************* quick search related **************/
    // Properly structured filter model
    const [filterModel, setFilterModel] = useState({
      items: [],
      quickFilterValues: []
    });
    const Footer = () =>{
        return(
            <Box
                sx={{
                    // width:"100%",
                    bgcolor: '#f5f5f5',
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Total Records: <strong>{data.length}</strong>
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                    Columns: <strong>{allColumns.length + 2}</strong>
                </Typography> */}
            </Box>
        )
    }
  const [isLoading,setIsLoading] = useState(false);
  const fetchServiceList = async () => {
    setIsLoading(true);
    const response = await SampleServicesCrud.allServices();
    if (response.success== '1') {
      setData(response.data.list);
      setIsLoading(false);
    }
  }
  useEffect(() => {
    fetchServiceList();
  },[])
  return (
    <>
      {isLoading && <Loading/>}
      <PageHeaderLayout headerTitle={"Services Sample Data"}>
        <Button
          size="small"
          variant="contained"
          onClick={() => navigate(-1)}
          endIcon={<ArrowBack sx={{ fontSize: ".85rem" }} />}
          sx={{ textTransform: "capitalize" }}
        >
          Back
        </Button>
      </PageHeaderLayout>

      <Box sx={{ overflow: "auto" }}>
        {/* {loading && <Loading />} */}
        <Box
          sx={{ width: "100%", display: "table", tableLayout: "fixed", backgroundColor: "#fff", borderRadius: ".5rem",}}
        >
          <Box
            sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", px: "1%", }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
                <MultiColumnFilter
                    filterableColumns={filterableColumns}
                    onApplyFilter={handleApplyFilter}
                    externalClear={clearMultiFilter}
                    defaultFilterOptions={[]}
                />
                {/* <ExportAll
                    paginationModel={paginationModel}
                    columns={columns}
                /> */}
                <Button 
                    onClick={openAddColumnDialog}
                    startIcon={<Add />}
                    sx={{textTransform:'none'}}
                >
                    Add New Column
                </Button>
            </Box>
            <QuickSearchTool filterModel={filterModel} setFilterModel={setFilterModel} />
          </Box>
          <DataGrid
            apiRef={apiRef}
            experimentalFeatures={{ newEditingApi: true }}
            onCellKeyDown={handleCellKeyDown}
            rows={rows}
            columns={columns}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            pageSizeOptions={[10, 25, 50, 100]}
            getCellClassName={(params) => {
              return params.isEditable ? 'editable-cell' : '';
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            filterModel={filterModel}
            filterMode="client"
            slots={{
                // toolbar: GridToolbar,
                noRowsOverlay: NoRowsLayout,
                loadingOverlay: Loading,
                pagination:Footer,
            }}
            columnResizeMode="growAndShrink"            
            disableColumnMenu
            
            sx={{
              height: "75vh",
              width: "100%",
              position: "relative",
              "& .MuiDataGrid-cell": {
                border: 1,
                borderRight: 0,
                borderTop: 0,
                borderColor: "lightgrey",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                whiteSpace: "nowrap",
                "&.MuiDataGrid-cell--editable": {
                  backgroundColor: "rgba(144, 202, 249, 0.08)",
                },
                textOverflow: "ellipsis",
                position: "relative",
                "&:hover": {
                  overflow: "visible",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  zIndex: 1,
                }
              },
              "& .MuiDataGrid-columnHeader": {
                position: "relative",
                "&:hover": {
                  "& .MuiDataGrid-columnSeparator": {
                    opacity: 1,
                    backgroundColor: "#1976d2",
                    width: "4px",
                  }
                }
              },
              "& .MuiDataGrid-columnSeparator": {
                transition: "all 0.2s",
                opacity: 0.5,
                position: "absolute",
                right: 0,
                height: "100%",
                width: "2px",
                cursor: "col-resize",
                "&:hover": {
                  opacity: 1,
                  backgroundColor: "#1976d2",
                  width: "4px",
                }
              },
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "2px solid rgba(224, 224, 224, 1)",
                borderTop: "2px solid rgba(224, 224, 224, 1)",
                fontWeight: 600,
                fontSize: "0.75rem",
                p: 0,
                minHeight: "0 !important",
                height: "2rem",
                "& .MuiDataGrid-columnHeader": {
                  borderLeft: "1px solid rgba(224, 224, 224, 1)",
                  position: "relative",
                  "&:last-child": {
                    borderRight: "none",
                  },
                  "&:hover .MuiDataGrid-columnSeparator": {
                    opacity: 1,
                    backgroundColor: "#1976d2",
                    width: "4px",
                  }
                },
                "& .MuiDataGrid-columnSeparator": {
                  transition: "all 0.2s",
                  opacity: 0.5,
                  position: "absolute",
                  right: 0,
                  height: "100%",
                  width: "2px",
                  cursor: "col-resize",
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: "#1976d2",
                    width: "4px",
                  }
                }
              },

              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "#3e3e3e",
              },
              "& .MuiDataGrid-row": {
                "&:last-child .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                },
                "& .MuiDataGrid-cell:last-child": {
                  borderRight: "none",
                },
              },
              "& .header-fixed": {
                // bgcolor: '#424242',
                color: "#000",
                fontWeight: "bold",
              },
              "& .header-dynamic": {
                // bgcolor: '#1976d2',
                color: "#000",
                fontWeight: "bold",
              },
              "& .cell-id": {
                bgcolor: "#f5f5f5",
                fontWeight: "bold",
                color: "text.secondary",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "2px solid #1976d2",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "2px solid #1976d2",
              },
              "& .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none",
              },
            }}
            // disableRowSelectionOnClick
          />
        </Box>
      </Box>
      {/* Add Column Dialog */}
      <Dialog open={addColDialogOpen} onClose={closeAddColumnDialog}>
        <DialogTitle>Add New Column</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a column name. This will add the parameter to each row's additionalData.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Column name"
            fullWidth
            variant="outlined"
            value={newColName}
            onChange={(e) => { setNewColName(e.target.value); setNewColError(''); }}
            error={!!newColError}
            helperText={newColError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddColumnDialog}>Cancel</Button>
          <Button onClick={confirmAddColumn} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          icon={<CheckIcon />}
        >
          Changes saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default DataGridEditor;
